// src/components/Podcast/PodcastForm.tsx - Enhanced version
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  FormHelperText,
  Paper,
  Tooltip,
  IconButton,
  InputAdornment,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  fetchPodcastById,
  createPodcast,
  updatePodcast,
  fetchPodcastTypes,
  Podcast,
  PodcastType,
  PodcastTimeSlot,
} from "../../services/api";
import { useSnackbar } from "notistack";
import PageHeader from "../common/PageHeader";

interface TimeSlot {
  startTime: string;
  endTime: string;
  id?: number;
}

interface FormData {
  title: string;
  shortDescription: string;
  longDescription: string;
  active: boolean;
  podcastTypeId: number;
  isDownloadable: boolean;
  timeSlots: TimeSlot[];
}

interface FormErrors {
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  podcastTypeId?: string;
  timeSlots?: { startTime?: string; endTime?: string }[];
}

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label: string;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  label,
}) => {
  // Parse the existing value (in HH:MM format)
  const parseTime = (timeStr: string) => {
    let hours = 12;
    let minutes = 0;
    let period = "AM";

    if (timeStr) {
      const parts = timeStr.split(":");
      if (parts.length >= 2) {
        const hourInt = parseInt(parts[0], 10);
        if (!isNaN(hourInt)) {
          if (hourInt === 0) {
            hours = 12;
            period = "AM";
          } else if (hourInt === 12) {
            hours = 12;
            period = "PM";
          } else if (hourInt > 12) {
            hours = hourInt - 12;
            period = "PM";
          } else {
            hours = hourInt;
            period = "AM";
          }
        }

        const minuteInt = parseInt(parts[1], 10);
        if (!isNaN(minuteInt)) {
          minutes = minuteInt;
        }
      }
    }

    return { hours, minutes, period };
  };

  // If value is empty, use default value of "09:00"
  const timeValue = value || "09:00";
  const { hours, minutes, period } = parseTime(timeValue);

  // Format time back to HH:MM (24-hour) format
  const formatTime = (hours: number, minutes: number, period: string) => {
    let hour24 = hours;

    if (period === "PM" && hours !== 12) {
      hour24 = hours + 12;
    } else if (period === "AM" && hours === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // When the component first renders with a default value but no actual value
  // set in the form data, trigger the onChange to save the default
  React.useEffect(() => {
    if (!value && timeValue) {
      onChange(timeValue);
    }
  }, [value, timeValue, onChange]);

  // Generate arrays for dropdown options
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

  const handleHourChange = (event: SelectChangeEvent<number>) => {
    const newHours = event.target.value as number;
    onChange(formatTime(newHours, minutes, period));
  };

  const handleMinuteChange = (event: SelectChangeEvent<number>) => {
    const newMinutes = event.target.value as number;
    onChange(formatTime(hours, newMinutes, period));
  };

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    const newPeriod = event.target.value;
    onChange(formatTime(hours, minutes, newPeriod));
  };

  return (
    <Box>
      <FormControl error={error} sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
            <Select
              value={hours}
              onChange={handleHourChange as any}
              disabled={disabled}
            >
              {hourOptions.map((hour) => (
                <MenuItem key={`hour-${hour}`} value={hour}>
                  {hour}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body1">:</Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
            <Select
              value={minutes}
              onChange={handleMinuteChange as any}
              disabled={disabled}
            >
              {minuteOptions.map((minute) => (
                <MenuItem key={`minute-${minute}`} value={minute}>
                  {minute.toString().padStart(2, "0")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
            <Select
              value={period}
              onChange={handlePeriodChange}
              disabled={disabled}
            >
              <MenuItem value="AM">AM</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

const PodcastForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<FormData>({
    title: "",
    shortDescription: "",
    longDescription: "",
    active: true,
    podcastTypeId: 0,
    isDownloadable: false,
    timeSlots: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const { data: podcastItem, isLoading: isPodcastLoading } = useQuery({
    queryKey: ["podcast", id],
    queryFn: () => fetchPodcastById(Number(id)),
    enabled: !!id,
  });

  const { data: podcastTypes, isLoading: isTypesLoading } = useQuery({
    queryKey: ["podcastTypes"],
    queryFn: fetchPodcastTypes,
  });

  useEffect(() => {
    if (podcastItem) {
      setFormData({
        ...podcastItem,
        timeSlots: podcastItem.timeSlots
          ? podcastItem.timeSlots.map((slot) => ({
              id: slot.id,
              startTime: formatTimeSpan(slot.startTime),
              endTime: formatTimeSpan(slot.endTime),
            }))
          : [],
        isDownloadable: podcastItem.isDownloadable ?? false,
      });
    }
  }, [podcastItem]);

  // Helper function to format TimeSpan to HH:MM format
  const formatTimeSpan = (timeSpan: string): string => {
    // If timeSpan is in TimeSpan format like "00:30:00"
    if (timeSpan.includes(":")) {
      const parts = timeSpan.split(":");
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
      }
    }
    return timeSpan;
  };

  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      // Create FormData for API call
      const formData = new FormData();

      // Add basic properties
      formData.append("title", data.title);
      formData.append("shortDescription", data.shortDescription);
      formData.append("longDescription", data.longDescription);
      formData.append("active", data.active.toString());
      formData.append("podcastTypeId", data.podcastTypeId.toString());
      formData.append("isDownloadable", data.isDownloadable.toString());

      // Add time slots
      data.timeSlots.forEach((slot, index) => {
        formData.append(
          `TimeSlots[${index}].StartTime`,
          formatTimeForApi(slot.startTime)
        );
        formData.append(
          `TimeSlots[${index}].EndTime`,
          formatTimeForApi(slot.endTime)
        );
        formData.append(`TimeSlots[${index}].DayOfWeek`, "0"); // Using Sunday (0) as default
      });

      if (id) {
        // For update, ensure we have the existing image URL
        if (podcastItem && podcastItem.imageUrl) {
          formData.append("existingImageUrl", podcastItem.imageUrl);
        }

        // Call the API with the formData
        return updatePodcast({
          id: Number(id),
          formData,
        });
      }

      // Create podcast
      return createPodcast(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["podcasts"] });
      navigate("/podcasts");
      enqueueSnackbar(
        id ? "Podcast updated successfully" : "Podcast created successfully",
        {
          variant: "success",
        }
      );
    },
    onError: (error) => {
      console.error("Error saving podcast:", error);
      enqueueSnackbar("Error saving podcast", { variant: "error" });
    },
  });

  // Helper function to format time values for API (ensure HH:MM:SS format)
  const formatTimeForApi = (timeString: string): string => {
    // If it's already in HH:MM:SS format, return as is
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }

    // If it's in HH:MM format, add seconds
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(":");
      return `${hours.padStart(2, "0")}:${minutes}:00`;
    }

    // Default fallback (should not reach here if validation is done properly)
    return timeString;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.shortDescription.trim())
      newErrors.shortDescription = "Short description is required";
    if (!formData.longDescription.trim())
      newErrors.longDescription = "Long description is required";
    if (formData.podcastTypeId === 0)
      newErrors.podcastTypeId = "Podcast type is required";

    // Validate time slots
    if (formData.timeSlots.length > 0) {
      const timeSlotErrors: { startTime?: string; endTime?: string }[] = [];
      let hasTimeSlotErrors = false;

      formData.timeSlots.forEach((slot, index) => {
        const slotError: { startTime?: string; endTime?: string } = {};

        if (!slot.startTime) {
          slotError.startTime = "Start time is required";
          hasTimeSlotErrors = true;
        }

        if (!slot.endTime) {
          slotError.endTime = "End time is required";
          hasTimeSlotErrors = true;
        }

        timeSlotErrors[index] = slotError;
      });

      if (hasTimeSlotErrors) {
        newErrors.timeSlots = timeSlotErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await mutation.mutateAsync(formData);
      } catch (error) {
        // Error handling is done in mutation's onError
      }
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: number | string } }
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTimeSlotChange = (
    index: number,
    field: "startTime" | "endTime",
    value: string
  ) => {
    setFormData((prev) => {
      const updatedTimeSlots = [...prev.timeSlots];
      updatedTimeSlots[index] = {
        ...updatedTimeSlots[index],
        [field]: value,
      };
      return { ...prev, timeSlots: updatedTimeSlots };
    });

    // Clear the error for this field if it exists
    if (
      errors.timeSlots &&
      errors.timeSlots[index] &&
      errors.timeSlots[index][field]
    ) {
      const updatedErrors = { ...errors };
      if (updatedErrors.timeSlots) {
        updatedErrors.timeSlots[index] = {
          ...updatedErrors.timeSlots[index],
          [field]: undefined,
        };
      }
      setErrors(updatedErrors);
    }
  };

  const addTimeSlot = () => {
    setFormData((prev) => ({
      ...prev,
      timeSlots: [
        ...prev.timeSlots,
        {
          startTime: "09:00",
          endTime: "10:00",
        },
      ],
    }));
  };

  const removeTimeSlot = (index: number) => {
    setFormData((prev) => {
      const updatedTimeSlots = [...prev.timeSlots];
      updatedTimeSlots.splice(index, 1);
      return { ...prev, timeSlots: updatedTimeSlots };
    });

    // Remove the errors for this slot if they exist
    if (errors.timeSlots) {
      const updatedErrors = { ...errors };
      if (updatedErrors.timeSlots) {
        updatedErrors.timeSlots.splice(index, 1);
        if (updatedErrors.timeSlots.length === 0) {
          delete updatedErrors.timeSlots;
        }
      }
      setErrors(updatedErrors);
    }
  };

  const handleCancel = () => {
    navigate("/podcasts");
  };

  if (isPodcastLoading || isTypesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={id ? "Edit Podcast" : "Create New Podcast"}
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Podcasts", path: "/podcasts" },
          { label: id ? "Edit Podcast" : "New Podcast" },
        ]}
        action={
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleCancel}
          >
            Back to List
          </Button>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, mb: 3 }}>
            <CardHeader title="Podcast Information" />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {mutation.isError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Error saving podcast. Please try again.
                  </Alert>
                )}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  disabled={mutation.isPending}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Short Description"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  error={!!errors.shortDescription}
                  helperText={errors.shortDescription}
                  disabled={mutation.isPending}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Long Description"
                  name="longDescription"
                  multiline
                  rows={6}
                  value={formData.longDescription}
                  onChange={handleChange}
                  error={!!errors.longDescription}
                  helperText={errors.longDescription}
                  disabled={mutation.isPending}
                />

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.podcastTypeId}
                >
                  <InputLabel id="podcastType-label">Podcast Type</InputLabel>
                  <Select
                    labelId="podcastType-label"
                    id="podcastTypeId"
                    name="podcastTypeId"
                    value={formData.podcastTypeId}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "podcastTypeId",
                          value: Number(e.target.value),
                        },
                      })
                    }
                    disabled={mutation.isPending}
                  >
                    <MenuItem value={0}>Select a type</MenuItem>
                    {podcastTypes?.map((type: PodcastType) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.podcastTypeId && (
                    <FormHelperText>{errors.podcastTypeId}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </CardContent>
          </Card>

          {/* Time Slots Card */}
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title="Time Slots"
              subheader="Add broadcast time slots for this podcast"
              action={
                <Button
                  startIcon={<AddIcon />}
                  color="primary"
                  onClick={addTimeSlot}
                  disabled={mutation.isPending}
                >
                  Add Time Slot
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {formData.timeSlots.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No time slots added. Click "Add Time Slot" to schedule
                    broadcast times.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {formData.timeSlots.map((slot, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => removeTimeSlot(index)}
                          disabled={mutation.isPending}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                      sx={{
                        bgcolor: "background.paper",
                        mb: 2,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        p: 2,
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <TimePicker
                            label="Start Time"
                            value={slot.startTime}
                            onChange={(value) =>
                              handleTimeSlotChange(index, "startTime", value)
                            }
                            disabled={mutation.isPending}
                            error={
                              !!(
                                errors.timeSlots &&
                                errors.timeSlots[index]?.startTime
                              )
                            }
                            helperText={
                              errors.timeSlots &&
                              errors.timeSlots[index]?.startTime
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TimePicker
                            label="End Time"
                            value={slot.endTime}
                            onChange={(value) =>
                              handleTimeSlotChange(index, "endTime", value)
                            }
                            disabled={mutation.isPending}
                            error={
                              !!(
                                errors.timeSlots &&
                                errors.timeSlots[index]?.endTime
                              )
                            }
                            helperText={
                              errors.timeSlots &&
                              errors.timeSlots[index]?.endTime
                            }
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              )}
              <FormHelperText>
                Add multiple time slots to schedule this podcast at different
                times
              </FormHelperText>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title="Settings"
              action={
                <Tooltip title="Configure podcast settings">
                  <IconButton>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleSwitchChange}
                      name="active"
                      disabled={mutation.isPending}
                      color="success"
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.active ? "Active" : "Inactive"}
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isDownloadable}
                      onChange={handleSwitchChange}
                      name="isDownloadable"
                      disabled={mutation.isPending}
                      color="primary"
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.isDownloadable
                        ? "Downloadable"
                        : "Not Downloadable"}
                    </Typography>
                  }
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={mutation.isPending}
                sx={{ mb: 2 }}
              >
                {mutation.isPending ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Saving...</span>
                  </Box>
                ) : id ? (
                  "Update Podcast"
                ) : (
                  "Create Podcast"
                )}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PodcastForm;
